import React, { useEffect, useState } from 'react'
import { FaUsers, FaUserPlus, FaInfoCircle } from 'react-icons/fa'
import { Table, Button, Badge, Card, Row, Col, Spinner } from 'react-bootstrap'
import { BotUser } from '../../../API/Users/models'
import { getBotUsers } from '../../../API/Users/Users'
import { EditUserModal } from './Components/EditUserModal'
import { DeleteUserModal } from './Components/DeleteUserModal'
import { CreateUserModal } from './Components/CreateUserModal'
import { Title } from '../../../../Templates/Title'
import { BsInfoCircle, BsCalendarWeek } from 'react-icons/bs'
import { ToggleBlockModal } from './Components/ToggleBlockModal'
import { getUserStatus } from './utils/getUserStatus'
import { MdRefresh } from 'react-icons/md'

export const Users = () => {
  const [users, setUsers] = useState<BotUser[]>([])
  const [lastUpdate, setLastUpdate] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)

  // Modals
  const [selectedUser, setSelectedUser] = useState<BotUser | null>(null)
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false)
  const [showEditUserModal, setShowEditUserModal] = useState(false)
  const [showCreateUserModal, setShowCreateUserModal] = useState(false)
  const [showToggleBlockModal, setShowToggleBlockModal] = useState(false)

  const fetchUsers = async () => {
    setIsLoading(true)
    try {
      const users = await getBotUsers()
      setUsers(users ?? [])
      setLastUpdate(new Date().toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' }))
    } catch (error) {
      console.error('Error al cargar usuarios:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!showDeleteUserModal && !showEditUserModal && !showCreateUserModal && !showToggleBlockModal) {
      fetchUsers()
    }
  }, [showDeleteUserModal, showEditUserModal, showCreateUserModal, showToggleBlockModal])

  return (
    <div className='pb-5 mt-4'>
      {/* Modales */}
      {selectedUser && <EditUserModal show={showEditUserModal} onHide={() => setShowEditUserModal(false)} user={selectedUser} />}
      {selectedUser && <DeleteUserModal show={showDeleteUserModal} onHide={() => setShowDeleteUserModal(false)} user={selectedUser} />}
      {showCreateUserModal && <CreateUserModal show={showCreateUserModal} onHide={() => setShowCreateUserModal(false)} />}
      {showToggleBlockModal && <ToggleBlockModal show={showToggleBlockModal} onHide={() => setShowToggleBlockModal(false)} user={selectedUser} />}

      {/* Encabezado */}
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <Title
          title='Usuarios'
          icon={<FaUsers size={24} />}
          subtitle='Gestiona los usuarios que utilizarán la aplicación'
        />
        <Button
          variant="primary"
          onClick={() => setShowCreateUserModal(true)}
          className="d-flex align-items-center gap-2"
        >
          <FaUserPlus size={16} />
          Nuevo usuario
        </Button>
      </div>

      {/* Información de actualización */}
      <Card className="bg-dark-75 mb-4">
        <Card.Body className="d-flex justify-content-between align-items-center py-2">
          <div className="d-flex align-items-center gap-2">
            <BsInfoCircle size={16} className="text-secondary" />
            <span className="text-white">
              Última actualización: {lastUpdate || '-'}
            </span>
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={fetchUsers}
            className="d-flex align-items-center gap-1"
            disabled={isLoading}
          >
            {isLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <MdRefresh size={16} />
            )}
            Actualizar
          </Button>
        </Card.Body>
      </Card>

      {isLoading && users.length === 0 ? (
        <div className="text-center p-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-secondary">Cargando usuarios...</p>
        </div>
      ) : users.length === 0 ? (
        <Card className="bg-dark-50 text-center p-5">
          <FaUsers size={60} className="text-secondary mb-3 mx-auto" />
          <h4>No hay usuarios registrados</h4>
          <p className="text-secondary mb-4">
            Crea tu primer usuario para comenzar a utilizar la aplicación
          </p>
          <Button
            variant="primary"
            className="mx-auto d-flex align-items-center gap-2 justify-content-center"
            style={{ width: 'fit-content' }}
            onClick={() => setShowCreateUserModal(true)}
          >
            <FaUserPlus size={16} />
            Crear usuario
          </Button>
        </Card>
      ) : (
        <Card className="bg-dark-75">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table variant="dark" className="mb-0" hover>
                <thead className="bg-darker">
                  <tr>
                    <th className='text-center'>Usuario</th>
                    <th className='text-center'>Último Ingreso</th>
                    <th className='text-center'>Última Interacción</th>
                    <th className='text-center'>Estado</th>
                    <th className='text-center'>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td className='align-middle'>
                        <div className='d-flex align-items-center'>
                          <div className='bg-primaryy rounded-circle d-flex align-items-center justify-content-center me-2'
                            style={{ width: '32px', height: '32px', minWidth: '32px' }}>
                            <span className='text-white fw-bold'>{user.name.charAt(0).toUpperCase()}</span>
                          </div>
                          <div>
                            <div className='fw-bold text-primary'>{user.name}</div>
                          </div>
                        </div>
                      </td>

                      <td className='text-center align-middle'>
                        {user?.loginDate ? (
                          <div className="d-flex flex-column">
                            <span>{new Date(user.loginDate).toLocaleDateString("es-AR")}</span>
                            <small className="text-secondary">{new Date(user.loginDate).toLocaleTimeString("es-AR", { hour: '2-digit', minute: '2-digit' })}</small>
                          </div>
                        ) : (
                          <span className="text-secondary">-</span>
                        )}
                      </td>

                      <td className='text-center align-middle'>
                        {user?.lastActionDate ? (
                          <div className="d-flex flex-column">
                            <span>{new Date(user.lastActionDate).toLocaleDateString("es-AR")}</span>
                            <small className="text-secondary">{new Date(user.lastActionDate).toLocaleTimeString("es-AR", { hour: '2-digit', minute: '2-digit' })}</small>
                          </div>
                        ) : (
                          <span className="text-secondary">-</span>
                        )}
                      </td>

                      <td className='text-center align-middle'>
                        <Badge
                          bg={getUserStatus(user)?.bg}
                          className={`px-2 py-1 ${getUserStatus(user)?.color}`}
                        >
                          {getUserStatus(user)?.text}
                        </Badge>
                      </td>

                      <td className='text-center align-middle'>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowEditUserModal(true);
                          }}
                        >
                          Gestionar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
          <div className="py-2">
            <div className='d-flex align-items-center justify-content-center gap-2 text-white mx-2 my-3 small'>
              <BsInfoCircle size={14} className='text-warning' />
              Se consideran usuarios activos aquellos que hayan interactuado en los últimos 10 minutos.
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}
